import { useMemo, useCallback } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
  type NodeTypes,
  type NodeProps,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Box, Shield, Layers, Database, Link2 } from "lucide-react";
import { useVisualizerStore } from "./store";

// ── Custom Node ──

const iconMap: Record<string, React.ElementType> = {
  root: Layers,
  component: Box,
  capability: Shield,
  import: Link2,
  resource: Database,
};

const bgMap: Record<string, string> = {
  root: "bg-indigo-600 dark:bg-indigo-600 text-white border-indigo-600 dark:border-indigo-600",
  capability: "bg-card text-foreground border-foreground",
  component: "bg-card text-foreground border-muted-foreground",
  import: "bg-muted d border-border",
  resource: "bg-muted d border-border",
};

function CustomNode({ data, selected }: NodeProps) {
  const Icon = iconMap[data.nodeType as string] || Box;
  const bg = bgMap[data.nodeType as string] || bgMap.component;

  return (
    <div
      className={`px-4 py-3 rounded-xl border-2 min-w-[180px] max-w-[260px] shadow-sm transition-shadow ${bg} ${
        selected ? "shadow-lg ring-2 ring-[#0f172a] ring-offset-2" : ""
      }`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-[#94a3b8] !w-2 !h-2 !border-0"
      />
      <div className="flex items-center gap-2">
        <Icon className="size-4 flex-shrink-0 opacity-70" />
        <div className="min-w-0">
          <p className="text-xs font-bold truncate leading-tight">
            {data.label as string}
          </p>
          {data.subtitle && (
            <p className="text-[10px] opacity-60 truncate mt-0.5">
              {data.subtitle as string}
            </p>
          )}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-[#94a3b8] !w-2 !h-2 !border-0"
      />
    </div>
  );
}

const nodeTypes: NodeTypes = {
  custom: CustomNode,
};

// ── Graph Builder ──

function buildGraph(
  components: ReturnType<typeof useVisualizerStore>["components"],
  capabilities: ReturnType<typeof useVisualizerStore>["capabilities"],
  resources: ReturnType<typeof useVisualizerStore>["resources"],
  raw: ReturnType<typeof useVisualizerStore>["raw"],
) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  if (!raw) return { nodes, edges };

  const def = raw["component-definition"];
  const title = def.metadata?.title || "Component Definition";

  // Root node
  nodes.push({
    id: "root",
    type: "custom",
    position: { x: 400, y: 0 },
    data: { label: title, nodeType: "root" },
  });

  // Capabilities row
  const capY = 120;
  const capSpacing = 300;
  const capStartX = 400 - ((capabilities.length - 1) * capSpacing) / 2;

  capabilities.forEach((cap, idx) => {
    const nodeId = `cap-${cap.uuid}`;
    nodes.push({
      id: nodeId,
      type: "custom",
      position: { x: capStartX + idx * capSpacing, y: capY },
      data: {
        label: cap.name,
        nodeType: "capability",
        subtitle: `${(cap["incorporates-components"] || []).length} components`,
      },
    });
    edges.push({
      id: `root-${nodeId}`,
      source: "root",
      target: nodeId,
      type: "smoothstep",
      style: { stroke: "#0f172a", strokeWidth: 2 },
      animated: true,
    });
  });

  // Components row
  const compY = capabilities.length > 0 ? 280 : 160;
  const compSpacing = 260;
  const compStartX = 400 - ((components.length - 1) * compSpacing) / 2;

  const typeSubtitle: Record<string, string> = {
    service: "Service",
    software: "Software",
    policy: "Policy",
    process: "Process",
    hardware: "Hardware",
  };

  components.forEach((comp, idx) => {
    const nodeId = `comp-${comp.uuid}`;
    nodes.push({
      id: nodeId,
      type: "custom",
      position: { x: compStartX + idx * compSpacing, y: compY },
      data: {
        label: comp.title,
        nodeType: "component",
        subtitle: typeSubtitle[comp.type] || comp.type,
      },
    });

    // If no capabilities, connect directly to root
    if (capabilities.length === 0) {
      edges.push({
        id: `root-${nodeId}`,
        source: "root",
        target: nodeId,
        type: "smoothstep",
        style: { stroke: "#64748b", strokeWidth: 1.5 },
      });
    }
  });

  // Capability → Component edges
  capabilities.forEach((cap) => {
    (cap["incorporates-components"] || []).forEach((ic) => {
      const capId = `cap-${cap.uuid}`;
      const compId = `comp-${ic["component-uuid"]}`;
      edges.push({
        id: `${capId}-${compId}`,
        source: capId,
        target: compId,
        type: "smoothstep",
        style: { stroke: "#64748b", strokeWidth: 1.5 },
        label: "incorporates",
        labelStyle: { fontSize: 9, fill: "#94a3b8" },
      });
    });
  });

  // Imports row (if any)
  const imports = def["import-component-definitions"] || [];
  if (imports.length > 0) {
    const impY = compY + 160;
    const impSpacing = 220;
    const impStartX = 400 - ((imports.length - 1) * impSpacing) / 2;

    imports.forEach((imp, idx) => {
      const name = imp.href.split("/").pop()?.replace(".json", "") || imp.href;
      const nodeId = `import-${idx}`;
      nodes.push({
        id: nodeId,
        type: "custom",
        position: { x: impStartX + idx * impSpacing, y: impY },
        data: {
          label: name,
          nodeType: "import",
          subtitle: "Imported Definition",
        },
      });
      edges.push({
        id: `root-${nodeId}`,
        source: "root",
        target: nodeId,
        type: "smoothstep",
        style: { stroke: "#cbd5e1", strokeWidth: 1, strokeDasharray: "5 5" },
      });
    });
  }

  return { nodes, edges };
}

// ── Inner Component (needs ReactFlowProvider wrapping) ──

function GraphInner() {
  const {
    components,
    capabilities,
    resources,
    raw,
    selectNode,
    selectedNodeId,
  } = useVisualizerStore();

  const { nodes, edges } = useMemo(
    () => buildGraph(components, capabilities, resources, raw),
    [components, capabilities, resources, raw],
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      selectNode(node.id);
    },
    [selectNode],
  );

  if (!raw) {
    return (
      <div className="flex flex-col items-center justify-center h-full dark:text-white/60 text-[#94a3b8]">
        <Layers className="size-16 mb-4 opacity-20" />
        <p className="text-lg font-bold dark:text-white/70 text-[#cbd5e1]">
          Component Definition Visualizer
        </p>
        <p className="text-sm mt-1">
          Import an OSCAL component-definition JSON to visualize relationships
        </p>
      </div>
    );
  }

  return (
    <ReactFlow
      nodes={nodes.map((n) => ({
        ...n,
        selected: n.id === selectedNodeId,
      }))}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodeClick={onNodeClick}
      fitView
      fitViewOptions={{ padding: 0.3 }}
      minZoom={0.2}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
    >
      <Background className="!stroke-border" gap={20} size={1} />
      <Controls
        showInteractive={false}
        className="!bg-card !border-border !shadow-sm [&>button]:!bg-card [&>button]:!border-border [&>button]:!d [&>button:hover]:!bg-accent"
      />
      <MiniMap
        nodeColor={() => "var(--foreground, #0f172a)"}
        maskColor="rgba(128,128,128,0.15)"
        className="!bg-card !border-border"
      />
    </ReactFlow>
  );
}

// ── Exported Component ──

export function ComponentGraph() {
  return (
    <ReactFlowProvider>
      <GraphInner />
    </ReactFlowProvider>
  );
}
